import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendSurveyAPI } from '../../services/api';
import { sendSurveyService, enhancedSendSurveyService } from '../../services/sendSurveyService';
import { parseApiError } from '../../utils/apiError';

import { 
  ArrowLeftIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  UsersIcon,
  ChartBarIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const SendSurvey = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, isCompanyAdmin, isAuthenticated } = useAuth();
  
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [surveys, setSurveys] = useState([]);
  
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [sendResults, setSendResults] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(10);
  const [deadline, setDeadline] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [divisionDropdownOpen, setDivisionDropdownOpen] = useState(false);
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);

  const companyDropdownRef = useRef(null);
  const branchDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const divisionDropdownRef = useRef(null);
  const surveyDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setCompanyDropdownOpen(false);
      }
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setBranchDropdownOpen(false);
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target)) {
        setDepartmentDropdownOpen(false);
      }
      if (divisionDropdownRef.current && !divisionDropdownRef.current.contains(event.target)) {
        setDivisionDropdownOpen(false);
      }
      if (surveyDropdownRef.current && !surveyDropdownRef.current.contains(event.target)) {
        setSurveyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated) {
      loadCompanies();
      loadSurveys();
    }
  }, [isAuthenticated]);

  // Load branches when company changes
  useEffect(() => {
    if (selectedCompany) {
      loadBranches(selectedCompany);
      setSelectedBranches([]);
      setSelectedDepartments([]);
      setSelectedDivisions([]);
      setEmployees([]);
    } else {
      setBranches([]);
      setSelectedBranches([]);
      setSelectedDepartments([]);
      setSelectedDivisions([]);
    }
  }, [selectedCompany]);

  // Load departments when branches change
  useEffect(() => {
    if (selectedBranches.length > 0) {
      loadDepartments(selectedBranches);
      setSelectedDepartments([]);
      setSelectedDivisions([]);
    } else {
      setDepartments([]);
      setSelectedDepartments([]);
      setSelectedDivisions([]);
    }
  }, [selectedBranches]);

  // Load divisions when departments change
  useEffect(() => {
    if (selectedDepartments.length > 0) {
      loadDivisions(selectedDepartments);
      setSelectedDivisions([]);
    } else {
      setDivisions([]);
      setSelectedDivisions([]);
    }
  }, [selectedDepartments]);

  // Load employees when filters change
  useEffect(() => {
    if (selectedCompany || selectedBranches.length > 0 || selectedDepartments.length > 0 || selectedDivisions.length > 0) {
      loadEmployees();
    }
  }, [selectedCompany, selectedBranches, selectedDepartments, selectedDivisions]);

  const loadCompanies = async () => {
    try {
      const response = await sendSurveyAPI.getCompanies();
      setCompanies(response.data);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    }
  };

  const loadSurveys = async () => {
    try {
      const response = await sendSurveyAPI.getSurveys();
      setSurveys(response.data);
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Failed to load surveys');
    }
  };

  const loadBranches = async (companyId) => {
    try {
      const response = await sendSurveyAPI.getBranches(companyId);
      setBranches(response.data);
    } catch (err) {
      console.error('Error loading branches:', err);
      setError('Failed to load branches');
    }
  };

  const loadDepartments = async (branchIds) => {
    try {
      const allDepartments = [];
      for (const branchId of branchIds) {
        const response = await sendSurveyAPI.getDepartments(branchId);
        allDepartments.push(...response.data);
      }
      setDepartments(allDepartments);
    } catch (err) {
      console.error('Error loading departments:', err);
      setError('Failed to load departments');
    }
  };

  const loadDivisions = async (departmentIds) => {
    try {
      const allDivisions = [];
      for (const departmentId of departmentIds) {
        const response = await sendSurveyAPI.getDivisions(departmentId);
        allDivisions.push(...response.data);
      }
      setDivisions(allDivisions);
    } catch (err) {
      console.error('Error loading divisions:', err);
      setError('Failed to load divisions');
    }
  };

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const params = {
        companyId: selectedCompany || undefined,
        branchIds: selectedBranches.length > 0 ? selectedBranches : undefined,
        departmentIds: selectedDepartments.length > 0 ? selectedDepartments : undefined,
        divisionIds: selectedDivisions.length > 0 ? selectedDivisions : undefined
      };
      
      const response = await sendSurveyAPI.getEmployees(params);
      setEmployees(response.data);
      setSelectedEmployees([]);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSurvey = async () => {
    if (!selectedSurvey || selectedEmployees.length === 0) {
      setError('Please select a survey and at least one employee');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      // Convert data to proper types
      const surveyId = parseInt(selectedSurvey, 10);
      const employeeIds = selectedEmployees.map(id => {
        // Handle both string and number IDs
        const numId = typeof id === 'string' ? parseInt(id, 10) : id;
        if (isNaN(numId)) {
          throw new Error(`Invalid employee ID: ${id} (type: ${typeof id})`);
        }
        return numId;
      });

      const requestData = {
        surveyId: surveyId,
        employeeIds: employeeIds,
        baseUrl: window.location.origin,
        estimatedTime: parseInt(estimatedTime, 10) || 10,
        deadline: deadline || null,
        message: customMessage || ''
      };

      // BUG FIX: this used to go through enhancedSendSurveyService.sendSurveyWithRetry,
      // which retries up to 3 times (with backoff) on ANY failure, including a
      // client-side timeout. POST /SendSurvey/send is NOT idempotent -- it
      // creates a new SurveyInvitation row and sends a real email for every
      // selected employee on every call, with no dedup/idempotency key. If the
      // server is just slow (e.g. sending real SMTP emails to many employees
      // one by one can easily take longer than the request timeout) rather
      // than actually failed, a "timeout" retry would silently re-send the
      // ENTIRE batch again -- duplicate invitations and duplicate emails to
      // everyone selected, not just whoever was slow. Calling the plain,
      // non-retrying method here instead.
      const response = await sendSurveyService.sendSurvey(requestData);

      setSendResults(response.data);

      // BUG FIX: this used to always show the green "sent successfully" banner
      // regardless of the actual counts, so "0 successful, 2 failed" showed up
      // under a success message -- exactly backwards. Mirror SendReminder.jsx's
      // logic: only call it a success if at least one invitation actually went
      // out, otherwise surface it as an error.
      if (response.data.totalSent > 0) {
        setSuccess(`Survey invitations sent! ${response.data.totalSent} successful, ${response.data.totalFailed} failed`);
        // Clear selections after at least a partial successful send
        setSelectedEmployees([]);
      } else {
        setError(`No survey invitations were sent successfully. ${response.data.totalFailed} failed.`);
      }
    } catch (err) {
      console.error('❌ Error sending survey:', err);
      setError(parseApiError(err, 'Failed to send survey invitations').message);
    } finally {
      setSending(false);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    setSelectedEmployees(
      selectedEmployees.length === filteredEmployees.length
        ? []
        : filteredEmployees.map(emp => emp.id)
    );
  };

  const handleBackToSurveys = () => {
    navigate('/surveys');
  };

  const clearFilters = () => {
    setSelectedCompany('');
    setSelectedBranches([]);
    setSelectedDepartments([]);
    setSelectedDivisions([]);
    setSearchTerm('');
    setSelectedEmployees([]);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Multi-select handlers for branches, departments, divisions
  const handleBranchSelect = (branchId) => {
    setSelectedBranches(prev => 
      prev.includes(branchId)
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    );
  };

  const handleDepartmentSelect = (departmentId) => {
    setSelectedDepartments(prev => 
      prev.includes(departmentId)
        ? prev.filter(id => id !== departmentId)
        : [...prev, departmentId]
    );
  };

  const handleDivisionSelect = (divisionId) => {
    setSelectedDivisions(prev => 
      prev.includes(divisionId)
        ? prev.filter(id => id !== divisionId)
        : [...prev, divisionId]
    );
  };

  const handleSelectAllBranches = () => {
    setSelectedBranches(
      selectedBranches.length === branches.length
        ? []
        : branches.map(b => b.id)
    );
  };

  const handleSelectAllDepartments = () => {
    setSelectedDepartments(
      selectedDepartments.length === departments.length
        ? []
        : departments.map(d => d.id)
    );
  };

  const handleSelectAllDivisions = () => {
    setSelectedDivisions(
      selectedDivisions.length === divisions.length
        ? []
        : divisions.map(d => d.id)
    );
  };

  // Company selection handler
  const handleCompanySelect = (companyId) => {
    setSelectedCompany(companyId);
    setCompanyDropdownOpen(false);
  };

  // Search and filter logic
  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(employee =>
        employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employeeCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [employees, searchTerm]);

  // Pagination logic
  const totalItems = filteredEmployees.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getSelectedCompanyName = () => {
    return companies.find(c => c.id === selectedCompany)?.name || 'Select Company';
  };

  const getSelectedBranchesText = () => {
    if (selectedBranches.length === 0) return 'Select Branches';
    if (selectedBranches.length === branches.length) return 'All Branches';
    if (selectedBranches.length === 1) {
      const branch = branches.find(b => b.id === selectedBranches[0]);
      return branch?.name || '1 Branch';
    }
    return `${selectedBranches.length} Branches`;
  };

  const getSelectedDepartmentsText = () => {
    if (selectedDepartments.length === 0) return 'Select Departments';
    if (selectedDepartments.length === departments.length) return 'All Departments';
    if (selectedDepartments.length === 1) {
      const department = departments.find(d => d.id === selectedDepartments[0]);
      return department?.name || '1 Department';
    }
    return `${selectedDepartments.length} Departments`;
  };

  const getSelectedDivisionsText = () => {
    if (selectedDivisions.length === 0) return 'Select Divisions';
    if (selectedDivisions.length === divisions.length) return 'All Divisions';
    if (selectedDivisions.length === 1) {
      const division = divisions.find(d => d.id === selectedDivisions[0]);
      return division?.name || '1 Division';
    }
    return `${selectedDivisions.length} Divisions`;
  };

  const getSelectedSurveyName = () => {
    return surveys.find(s => s.id === parseInt(selectedSurvey))?.name || 'Select Survey';
  };

  // Custom Dropdown Component for multi-select
  const MultiSelectDropdown = ({ 
    isOpen, 
    setIsOpen, 
    options, 
    selectedValues, 
    onSelect, 
    onSelectAll,
    getDisplayText,
    placeholder,
    disabled,
    type
  }) => (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 text-left border rounded-lg transition-colors flex items-center justify-between ${
          disabled 
            ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' 
            : 'border-gray-300 bg-white hover:bg-gray-50'
        }`}
      >
        <span className="truncate">{getDisplayText()}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {/* Select All Option */}
          {options.length > 0 && (
            <button
              onClick={onSelectAll}
              className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 flex items-center gap-3"
            >
              <div className={`w-4 h-4 border border-gray-300 rounded flex items-center justify-center ${
                selectedValues.length === options.length ? 'bg-blue-500 border-blue-500' : 'bg-white'
              }`}>
                {selectedValues.length === options.length && (
                  <CheckIcon className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="text-sm font-medium text-gray-700">
                {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
              </span>
            </button>
          )}
          
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => onSelect(option.id)}
              className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-3"
            >
              <div className={`w-4 h-4 border border-gray-300 rounded flex items-center justify-center ${
                selectedValues.includes(option.id) ? 'bg-blue-500 border-blue-500' : 'bg-white'
              }`}>
                {selectedValues.includes(option.id) && (
                  <CheckIcon className="w-3 h-3 text-white" />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium text-gray-700">{option.name}</div>
                {option.companyName && (
                  <div className="text-xs text-gray-500 mt-1">Company: {option.companyName}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Please log in to send surveys</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToSurveys}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Surveys"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Send Survey Invitations
              </h1>
              <p className="text-gray-600">
                Send survey invitations to employees with unique encoded links
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full font-medium text-sm">
              {selectedEmployees.length} Selected
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Organization Filters Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BuildingOfficeIcon className="w-5 h-5 text-blue-500" />
          Select Organization
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Company Dropdown - Single Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
            <div ref={companyDropdownRef}>
              <button
                onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
                className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span className="truncate">{getSelectedCompanyName()}</span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {companyDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleCompanySelect(company.id)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                        selectedCompany === company.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      <div className="font-medium">{company.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Branch Dropdown - Multi Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
            <div ref={branchDropdownRef}>
              <MultiSelectDropdown
                isOpen={branchDropdownOpen}
                setIsOpen={setBranchDropdownOpen}
                options={branches}
                selectedValues={selectedBranches}
                onSelect={handleBranchSelect}
                onSelectAll={handleSelectAllBranches}
                getDisplayText={getSelectedBranchesText}
                placeholder="Select Branches"
                disabled={!selectedCompany}
                type="branch"
              />
            </div>
          </div>

          {/* Department Dropdown - Multi Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <div ref={departmentDropdownRef}>
              <MultiSelectDropdown
                isOpen={departmentDropdownOpen}
                setIsOpen={setDepartmentDropdownOpen}
                options={departments}
                selectedValues={selectedDepartments}
                onSelect={handleDepartmentSelect}
                onSelectAll={handleSelectAllDepartments}
                getDisplayText={getSelectedDepartmentsText}
                placeholder="Select Departments"
                disabled={selectedBranches.length === 0}
                type="department"
              />
            </div>
          </div>

          {/* Division Dropdown - Multi Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Division</label>
            <div ref={divisionDropdownRef}>
              <MultiSelectDropdown
                isOpen={divisionDropdownOpen}
                setIsOpen={setDivisionDropdownOpen}
                options={divisions}
                selectedValues={selectedDivisions}
                onSelect={handleDivisionSelect}
                onSelectAll={handleSelectAllDivisions}
                getDisplayText={getSelectedDivisionsText}
                placeholder="Select Divisions"
                disabled={selectedDepartments.length === 0}
                type="division"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-600">
            {employees.length} employees found
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Survey Selection Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-blue-500" />
          Select Survey
        </h2>
        
        <div className="relative">
          <div ref={surveyDropdownRef}>
            <button
              onClick={() => setSurveyDropdownOpen(!surveyDropdownOpen)}
              className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span className="truncate">{getSelectedSurveyName()}</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${surveyDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {surveyDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                {surveys.map((survey) => (
                  <button
                    key={survey.id}
                    onClick={() => {
                      setSelectedSurvey(survey.id.toString());
                      setSurveyDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                      selectedSurvey === survey.id.toString() ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{survey.name}</div>
                    <div className="text-sm text-gray-500 mt-1">{survey.description}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Company: {survey.companyName}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Employees Selection Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-blue-500" />
              Select Employees ({selectedEmployees.length} selected)
            </h2>
            <p className="text-sm text-gray-600">
              {filteredEmployees.length} of {employees.length} employees
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
          </div>

          <div className="flex gap-3 w-full lg:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 lg:flex-none lg:w-80">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search employees by name, email, or position..." 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              onClick={handleSelectAll}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors whitespace-nowrap"
            >
              {selectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {/* Employees List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading employees...</span>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <UserGroupIcon className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Select organization filters to view employees'
              }
            </p>
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {currentEmployees.map((employee) => (
              <div 
                key={employee.id}
                className={`border rounded-lg transition-all duration-200 ${
                  selectedEmployees.includes(employee.id)
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:shadow-md'
                }`}
              >
                <div className="p-4 flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={selectedEmployees.includes(employee.id)}
                    onChange={() => handleEmployeeSelect(employee.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {employee.firstName?.[0]}{employee.lastName?.[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{employee.position}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <EnvelopeIcon className="w-4 h-4" />
                        {employee.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPinIcon className="w-4 h-4" />
                        {employee.branchName}
                      </span>
                      <span className="flex items-center gap-1">
                        <BuildingOfficeIcon className="w-4 h-4" />
                        {employee.departmentName}
                      </span>
                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                        {employee.employeeCode}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredEmployees.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} employees
            </div>

            <div className="flex items-center gap-4">
              <div className="flex gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    Math.abs(page - currentPage) <= 1
                  )
                  .map((page, index, array) => {
                    const showEllipsis = index < array.length - 1 && array[index + 1] - page > 1;
                    return (
                      <React.Fragment key={page}>
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 text-sm border rounded transition-colors ${
                            currentPage === page
                              ? 'bg-blue-500 text-white border-blue-500'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                        {showEllipsis && (
                          <span className="px-2 text-gray-500">...</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Send Configuration Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-blue-500" />
          Survey Configuration
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated Time (minutes)
            </label>
            <input
              type="number"
              value={estimatedTime}
              onChange={(e) => setEstimatedTime(parseInt(e.target.value) || 10)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              min="1"
              max="120"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deadline (optional)
            </label>
            <input
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Message (optional)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a custom message for the survey invitation..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={handleBackToSurveys}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
        >
          Cancel
        </button>
        
        <button
          onClick={handleSendSurvey}
          disabled={sending || !selectedSurvey || selectedEmployees.length === 0}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 transition-colors"
        >
          {sending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Sending...
            </>
          ) : (
            <>
              <EnvelopeIcon className="w-5 h-5" />
              Send Survey Invitations ({selectedEmployees.length})
            </>
          )}
        </button>
      </div>

      {/* Send Results */}
      {sendResults && (
        <div className="mt-6 bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Results</h3>
          <div className="space-y-3">
            {sendResults.results.map((result, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <span className="font-medium">{result.employeeName}</span>
                  <span className="text-gray-600 ml-2">({result.email})</span>
                  {/* Show the real failure reason (e.g. "No active email setup
                      found for company 3") now that the backend sends it,
                      instead of only a "Failed" badge with no explanation. */}
                  {!result.success && result.message && (
                    <div className="text-sm text-red-600 mt-1">{result.message}</div>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  result.success 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.success ? 'Sent' : 'Failed'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SendSurvey;